import { useState, Fragment } from "react";
import { Factory, Boxes, BarChart3 } from "lucide-react";
import { useSetPageHeader } from "@/components/layout/PageHeaderContext";
import { KPICard } from "@/components/dashboard/KPICard";
import { FilterBar } from "@/components/dashboard/FilterBar";
import { ProductSelector } from "@/components/dashboard/ProductSelector";
import { ProductionTrend } from "@/components/charts/ProductionTrend";
import {
  useProductionSummary,
  useProductionDaily,
  useProductionProducts,
  useProductionRuns,
  useRunMaterials,
} from "@/hooks/use-production";
import type { RunMaterial } from "@shared/types";
import { formatNumber, formatDate, daysAgo, today } from "@/lib/utils";
import { SkeletonChart, SkeletonTable } from "@/components/ui/Skeleton";

export function Production() {
  useSetPageHeader({
    title: "Production",
    subtitle: "Run throughput, volume trends, and part mix",
    icon: Factory,
  });

  const [from, setFrom] = useState(() => daysAgo(90));
  const [to, setTo] = useState(() => today());
  const [part, setPart] = useState("");
  const [page, setPage] = useState(1);
  const [expandedRun, setExpandedRun] = useState<number | null>(null);

  const summary = useProductionSummary(from, to);
  const daily = useProductionDaily(from, to, part || undefined);
  const parts = useProductionProducts();
  const runs = useProductionRuns(from, to, part || undefined, page);
  const runMaterials = useRunMaterials(expandedRun);

  const s = summary.data;
  const runTrend = s
    ? ((s.total_runs - s.prev_total_runs) / (s.prev_total_runs || 1)) * 100
    : 0;
  const qtyTrend = s
    ? ((s.total_quantity - s.prev_total_quantity) / (s.prev_total_quantity || 1)) * 100
    : 0;

  return (
    <div className="space-y-6">
      <FilterBar from={from} to={to} onFromChange={setFrom} onToChange={setTo}>
        <ProductSelector
          products={
            parts.data?.map((p) => ({
              value: p.slug,
              label: p.name,
            })) || []
          }
          value={part}
          onChange={setPart}
        />
      </FilterBar>

      {/* KPI cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KPICard
          title="Total Runs"
          value={s ? formatNumber(s.total_runs) : "..."}
          trend={runTrend}
          trendLabel="vs prev period"
          icon={<Factory className="h-5 w-5" />}
        />
        <KPICard
          title="Total Output"
          value={s ? formatNumber(s.total_quantity) : "..."}
          trend={qtyTrend}
          trendLabel="vs prev period"
          icon={<Factory className="h-5 w-5" />}
        />
        <KPICard
          title="Active Parts"
          value={s ? String(s.active_parts) : "..."}
          icon={<Boxes className="h-5 w-5" />}
        />
        <KPICard
          title="Daily Average"
          value={s ? formatNumber(s.avg_daily_output) : "..."}
          subtitle="parts per day"
          icon={<BarChart3 className="h-5 w-5" />}
        />
      </div>

      {/* Production trend chart */}
      <div className="rounded-xl border border-border bg-card p-5">
        <h2 className="mb-4 text-lg font-semibold text-card-foreground">
          Daily Production Volume
        </h2>
        {daily.isLoading ? (
          <SkeletonChart height={320} />
        ) : (
          <ProductionTrend data={daily.data || []} />
        )}
      </div>

      {/* Part mix */}
      {parts.data && parts.data.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-5">
          <h2 className="mb-4 text-lg font-semibold text-card-foreground">
            Part Mix
          </h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {parts.data.slice(0, 9).map((p) => (
              <div
                key={p.slug}
                className="flex items-center justify-between rounded-lg border border-border/50 p-3"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-foreground">
                    {p.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatNumber(p.run_count)} runs
                  </p>
                </div>
                <p className="ml-3 text-sm font-semibold text-foreground">
                  {formatNumber(p.total_quantity)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Runs table */}
      <div className="rounded-xl border border-border bg-card p-5">
        <h2 className="mb-4 text-lg font-semibold text-card-foreground">
          Recent Runs
        </h2>
        {runs.isLoading ? (
          <SkeletonTable rows={8} />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="px-3 py-2 text-muted-foreground font-medium">Run #</th>
                    <th className="px-3 py-2 text-muted-foreground font-medium">Part</th>
                    <th className="px-3 py-2 text-muted-foreground font-medium">Machine</th>
                    <th className="px-3 py-2 text-right text-muted-foreground font-medium">Good</th>
                    <th className="px-3 py-2 text-right text-muted-foreground font-medium">Rejected</th>
                    <th className="px-3 py-2 text-muted-foreground font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.data?.data.map((b) => {
                    const isExpanded = expandedRun === b.id;
                    return (
                      <Fragment key={b.id}>
                        <tr
                          className={`border-b border-border/50 cursor-pointer transition-colors ${isExpanded ? "bg-muted/70" : "hover:bg-muted/50"}`}
                          onClick={() => setExpandedRun(isExpanded ? null : b.id)}
                        >
                          <td className="px-3 py-2 font-mono text-xs">
                            <span className="mr-1.5 inline-block w-3 text-xs text-muted-foreground/60">{isExpanded ? "▾" : "▸"}</span>
                            {b.run_number}
                          </td>
                          <td className="px-3 py-2">{b.part_name}</td>
                          <td className="px-3 py-2 text-muted-foreground">{b.machine_code}</td>
                          <td className="px-3 py-2 text-right">{formatNumber(b.good_parts)}</td>
                          <td className="px-3 py-2 text-right">{formatNumber(b.rejected_parts)}</td>
                          <td className="px-3 py-2 text-muted-foreground">{formatDate(b.run_date)}</td>
                        </tr>
                        {isExpanded && (
                          <tr className="border-b border-border/50">
                            <td colSpan={6} className="px-3 py-3 bg-muted/30">
                              {runMaterials.isLoading ? (
                                <p className="text-xs text-muted-foreground">Loading materials...</p>
                              ) : runMaterials.data && runMaterials.data.length > 0 ? (
                                <MaterialBreakdown materials={runMaterials.data} />
                              ) : (
                                <p className="text-xs text-muted-foreground">No material records for this run.</p>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {runs.data && runs.data.total > runs.data.limit && (
              <div className="mt-4 flex items-center justify-between text-sm">
                <p className="text-muted-foreground">
                  Showing {(page - 1) * runs.data.limit + 1}–
                  {Math.min(page * runs.data.limit, runs.data.total)} of{" "}
                  {formatNumber(runs.data.total)}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="rounded-md border border-input px-3 py-1 text-foreground disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page * runs.data.limit >= runs.data.total}
                    className="rounded-md border border-input px-3 py-1 text-foreground disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function MaterialBreakdown({ materials }: { materials: RunMaterial[] }) {
  return (
    <div>
      <p className="mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
        Material Breakdown ({materials.length} ingredients)
      </p>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border/30 text-left">
            <th className="pb-1 pr-3 text-muted-foreground font-medium">Material</th>
            <th className="pb-1 pr-3 text-right text-muted-foreground font-medium">Target (%)</th>
            <th className="pb-1 pr-3 text-right text-muted-foreground font-medium">Actual (%)</th>
            <th className="pb-1 text-right text-muted-foreground font-medium">Variance</th>
          </tr>
        </thead>
        <tbody>
          {materials.map((m) => (
            <tr key={m.material_name} className="border-b border-border/20">
              <td className="py-1 pr-3 font-medium text-foreground">{m.material_name}</td>
              <td className="py-1 pr-3 text-right text-muted-foreground">{m.target_pct.toFixed(2)}</td>
              <td className="py-1 pr-3 text-right">{m.actual_pct.toFixed(2)}</td>
              <td className={`py-1 text-right font-medium ${
                Math.abs(m.variance_pct) > 5 ? "text-red-400" : Math.abs(m.variance_pct) > 2 ? "text-amber-400" : "text-muted-foreground"
              }`}>
                {m.variance_pct > 0 ? "+" : ""}{m.variance_pct.toFixed(2)}%
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
